import { useState, useRef, useEffect } from 'react';
import { Sidebar, useSidebar } from '@/components/Sidebar';
import {
  Send,
  User as UserIcon,
  Loader2,
  Lightbulb,
  TrendingUp,
  PiggyBank,
  CreditCard,
  ArrowUpRight,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { useTranslation } from '@/hooks/useTranslation';
import { useAiChat } from '@/hooks/useAiChat';
import { cn } from '@/lib/utils';
import cleviaIcon from '@/assets/clevia-icon.png';
import type { TranslationKey } from '@/lib/i18n';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const SUGGESTED_PROMPTS: { icon: typeof TrendingUp; titleKey: TranslationKey; prompt: string }[] = [
  {
    icon: TrendingUp,
    titleKey: 'ai.prompts.spending',
    prompt: 'spending_analysis',
  },
  {
    icon: PiggyBank,
    titleKey: 'ai.prompts.savings',
    prompt: 'savings_tips',
  },
  {
    icon: CreditCard,
    titleKey: 'ai.prompts.subscriptions',
    prompt: 'subscriptions_review',
  },
  {
    icon: Lightbulb,
    titleKey: 'ai.prompts.advice',
    prompt: 'financial_advice',
  },
];

export default function AiAssistantPage() {
  const { collapsed: sidebarCollapsed } = useSidebar();
  const { t } = useTranslation();
  const { sendMessage: sendAiMessage } = useAiChat();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Pass conversation history for context retention
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const response = await sendAiMessage(content.trim(), history);

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: t('ai.error.generic'),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedPrompt = (promptKey: string) => {
    const promptText = t(`ai.prompts.${promptKey}.full` as TranslationKey);
    handleSendMessage(promptText);
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117]">
      <Sidebar activePage="coach" />
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}>
        <PageHeader title={t('coach.title')} subtitle={t('coach.subtitle')} />

        {/* Chat Container */}
        <div className="flex-1 flex flex-col overflow-hidden p-6">
          <div className="flex-1 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 shadow-sm overflow-hidden flex flex-col">
            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {messages.length === 0 ? (
                // Empty state with suggestions
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-20 h-20 bg-white dark:bg-gray-700 rounded-3xl flex items-center justify-center mb-6 shadow-lg shadow-[#16c47f]/20 overflow-hidden">
                    <img src={cleviaIcon} alt="clevia" className="w-16 h-16 object-contain" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t('ai.welcome.title')}
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md">
                    {t('ai.welcome.description')}
                  </p>

                  {/* Suggested Prompts */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg w-full">
                    {SUGGESTED_PROMPTS.map((prompt, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestedPrompt(prompt.prompt)}
                        className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 transition-all duration-200 text-left group"
                      >
                        <div className="w-10 h-10 bg-[#16c47f]/10 dark:bg-[#16c47f]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <prompt.icon className="w-5 h-5 text-[#16c47f]" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200 flex-1">
                          {t(prompt.titleKey)}
                        </span>
                        <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-[#16c47f] transition-colors" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                // Messages list
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        'flex gap-4',
                        message.role === 'user' ? 'flex-row-reverse' : ''
                      )}
                    >
                      {/* Avatar */}
                      <div
                        className={cn(
                          'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden',
                          message.role === 'user' ? 'bg-[#16c47f]' : 'bg-white dark:bg-gray-600'
                        )}
                      >
                        {message.role === 'user' ? (
                          <UserIcon className="w-5 h-5 text-white" />
                        ) : (
                          <img src={cleviaIcon} alt="clevia" className="w-8 h-8 object-contain" />
                        )}
                      </div>

                      {/* Message Bubble */}
                      <div
                        className={cn(
                          'max-w-[70%] rounded-2xl px-4 py-3',
                          message.role === 'user'
                            ? 'bg-[#16c47f] text-white rounded-tr-sm'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-tl-sm'
                        )}
                      >
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {message.content}
                        </p>
                        <p
                          className={cn(
                            'text-xs mt-2',
                            message.role === 'user'
                              ? 'text-white/70'
                              : 'text-gray-500 dark:text-gray-400'
                          )}
                        >
                          {message.timestamp.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Loading indicator */}
                  {isLoading && (
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-xl bg-white dark:bg-gray-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <img src={cleviaIcon} alt="clevia" className="w-8 h-8 object-contain" />
                      </div>
                      <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-tl-sm px-4 py-3">
                        <div className="flex gap-1.5">
                          <div
                            className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"
                            style={{ animationDelay: '0ms' }}
                          />
                          <div
                            className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"
                            style={{ animationDelay: '150ms' }}
                          />
                          <div
                            className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"
                            style={{ animationDelay: '300ms' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input Area */}
            <div className="border-t border-gray-100 dark:border-gray-700/50 p-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      if (e.target.value.length <= 200) {
                        setInputValue(e.target.value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSendMessage(inputValue);
                      }
                    }}
                    placeholder={t('ai.input.placeholder')}
                    maxLength={200}
                    className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-4 py-3 pr-16 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#16c47f] focus:border-transparent transition-all"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    {inputValue.length}/200
                  </span>
                </div>

                <button
                  onClick={() => handleSendMessage(inputValue)}
                  disabled={!inputValue.trim() || isLoading}
                  className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 flex-shrink-0',
                    inputValue.trim() && !isLoading
                      ? 'bg-[#16c47f] hover:bg-[#12b070] text-white shadow-lg shadow-[#16c47f]/20'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  )}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-3">
                {t('ai.disclaimer')}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
